import { DatePickerDropdown } from '../DatePickerDropdown';
import { parseLocalDate } from '../utils/date';
import type { AssignedUser, TaskNote, TaskObject } from '../taskEditModal.types';

// Mirrors KanbanModule's initials logic so the modal and board agree.
function getInitialsForModal(name: string): string {
  if (!name) return '??';
  if (name.includes('@')) {
    const localPart = name.split('@')[0];
    return localPart.slice(0, 2).toUpperCase();
  }
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

// Hash by name/email (not userId) so color stays consistent across views.
function getUserColorForModal(nameOrEmail: string): string {
  const colors = ['var(--color-primary)', 'var(--color-accent-strong)', 'var(--color-accent-bright)', 'var(--color-warning)', 'var(--color-success)', 'var(--color-accent)', 'var(--color-accent-strong)'];
  const normalized = (nameOrEmail || '').toLowerCase();
  const hash = normalized.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return colors[hash % colors.length];
}

interface TaskViewOnlyBodyProps {
  task: TaskObject;
  owner: AssignedUser | null;
  dueAt: string;
  notes: TaskNote[];
  conversationMembers: any[];
  showOwnerPicker: boolean;
  showDatePicker: boolean;
  setShowOwnerPicker: (show: boolean) => void;
  setShowDatePicker: (show: boolean) => void;
  onViewOnlySave: (field: 'owner' | 'dueAt', value: AssignedUser | null | string) => void;
}

/**
 * Read-only render branch of the Task Edit Modal (used from the Activity Feed):
 * assigned-to picker, due-by picker, created-by/on block, notes display.
 */
export function TaskViewOnlyBody({
  task,
  owner,
  dueAt,
  notes,
  conversationMembers,
  showOwnerPicker,
  showDatePicker,
  setShowOwnerPicker,
  setShowDatePicker,
  onViewOnlySave,
}: TaskViewOnlyBodyProps) {
  return (
    <div className="px-4 pt-[10px] pb-4 space-y-2 overflow-visible flex-1">
      <div className="flex gap-2 text-sm relative">
        <span className="font-medium text-gray-500 w-24 shrink-0">Assigned to:</span>
        {showOwnerPicker ? (
          <div className="flex-1">
            <select
              value={owner?.id || ''}
              onChange={(e) => {
                const memberId = e.target.value;
                if (!memberId) {
                  onViewOnlySave('owner', null);
                } else {
                  const member = conversationMembers.find((m: any) => m.id === memberId);
                  if (member) {
                    const newOwner = { id: memberId, name: member.name || member.email };
                    onViewOnlySave('owner', newOwner);
                  }
                }
                setShowOwnerPicker(false);
              }}
              onBlur={() => setShowOwnerPicker(false)}
              autoFocus
              className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
              style={{ outline: 'none' }}
            >
              <option value="">Unassigned</option>
              {conversationMembers.map((member: any) => (
                <option key={member.id} value={member.id}>
                  {member.name || member.email}
                </option>
              ))}
            </select>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setShowOwnerPicker(true)}
            className="flex items-center gap-1.5 hover:bg-gray-100 rounded px-1 -mx-1 transition-colors"
          >
            {owner ? (
              <>
                <div
                  style={{
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    backgroundColor: getUserColorForModal(owner.name),
                    color: 'white',
                    fontSize: '9px',
                    fontWeight: 500,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                  title={owner.name}
                >
                  {getInitialsForModal(owner.name)}
                </div>
                <span className="text-gray-900">{owner.name}</span>
              </>
            ) : (
              <span className="text-blue-600 hover:text-blue-700">Pick Team Member</span>
            )}
          </button>
        )}
      </div>

      <div className="flex gap-2 text-sm relative">
        <span className="font-medium text-gray-500 w-24 shrink-0">Due by:</span>
        {showDatePicker ? (
          <div className="flex-1">
            <DatePickerDropdown
              value={dueAt}
              onChange={(newDate) => {
                onViewOnlySave('dueAt', newDate);
                setShowDatePicker(false);
              }}
              onClear={() => {
                onViewOnlySave('dueAt', '');
                setShowDatePicker(false);
              }}
            />
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setShowDatePicker(true)}
            className={`hover:bg-gray-100 rounded px-1 -mx-1 transition-colors ${dueAt ? 'text-black' : 'text-blue-600 hover:text-blue-700'}`}
          >
            {dueAt ? parseLocalDate(dueAt).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' }) : 'Add date'}
          </button>
        )}
      </div>

      <div className="mt-3 pt-3 border-t border-gray-100 space-y-1">
        <div className="flex gap-2 text-xs text-gray-400">
          <span className="w-24 shrink-0">Created by:</span>
          <span>{task.authorName || 'Unknown'}</span>
        </div>
        <div className="flex gap-2 text-xs text-gray-400">
          <span className="w-24 shrink-0">Created on:</span>
          <span>
            {task.createdAt
              ? `${new Date(task.createdAt).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })} at ${new Date(task.createdAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`
              : 'Unknown'}
          </span>
        </div>
      </div>

      {notes.length > 0 && (
        <div className="pt-2 border-t mt-3">
          <div className="text-sm font-medium text-gray-500 mb-2">Notes</div>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {notes.map((noteObj) => (
              <div key={noteObj.id} className="p-2 bg-gray-50 rounded-lg">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs text-gray-600 font-medium">
                    {noteObj.authorName || 'Unknown'} — {new Date(noteObj.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} · {new Date(noteObj.createdAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                    {noteObj.updatedAt && noteObj.updatedAt !== noteObj.createdAt && ' (edited)'}
                  </span>
                </div>
                <p className="text-sm text-gray-900 whitespace-pre-wrap">{noteObj.content}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
